import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost } from "../lib/api.js";

/**
 * The stop/start control and state chip — docs/01-PRODUCT-SPEC.md §5.
 *
 * The stop button is the "big red button" a coordinator reaches for when something is going wrong
 * on deadline day. It asks for a typed confirmation and a reason, because six months later "why
 * was this closed on the 14th?" is a question only the audit log can answer.
 */

type AvailabilityState =
  "OPEN" | "NOT_YET_OPEN" | "CLOSED_SCHEDULE" | "CLOSED_MANUAL" | "FULL" | "ARCHIVED" | "DRAFT";

interface AvailabilityResponse {
  availability: {
    state: AvailabilityState;
    opensAt: string | null;
    closesAt: string | null;
    message: string | null;
  };
  pauseReason: string | null;
  maxTotalSubmissions: number | null;
  submissionCount: number;
  nextChange: { at: string; becomes: "OPEN" | "CLOSED_SCHEDULE" } | null;
}

const STATE_CHIP: Record<AvailabilityState, { label: string; className: string }> = {
  OPEN: { label: "Open", className: "bg-accent/20 text-primary" },
  NOT_YET_OPEN: { label: "Scheduled", className: "bg-muted text-muted-foreground" },
  CLOSED_SCHEDULE: { label: "Closed (schedule)", className: "bg-muted text-muted-foreground" },
  CLOSED_MANUAL: { label: "Closed (stopped)", className: "bg-brand-red/10 text-brand-red" },
  FULL: { label: "Full", className: "bg-brand-red/10 text-brand-red" },
  ARCHIVED: { label: "Archived", className: "bg-muted text-muted-foreground" },
  DRAFT: { label: "Draft", className: "bg-muted text-muted-foreground" },
};

/** Counts down to the next automatic change, in the coordinator's own reading of the clock. */
const useCountdown = (target: string | null): string | null => {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!target) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [target]);

  if (!target) return null;
  const remaining = new Date(target).getTime() - now;
  if (remaining <= 0) return "any moment";

  const days = Math.floor(remaining / 86_400_000);
  const hours = Math.floor((remaining % 86_400_000) / 3_600_000);
  const minutes = Math.floor((remaining % 3_600_000) / 60_000);
  const seconds = Math.floor((remaining % 60_000) / 1000);

  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m ${seconds}s`;
};

export const AvailabilityPanel = ({ formId }: { formId: string }): JSX.Element => {
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const [reason, setReason] = useState("");

  const query = useQuery({
    queryKey: ["availability", formId],
    queryFn: () => apiGet<AvailabilityResponse>(`/forms/${formId}/availability`),
    // Availability is computed on every request, so a stale chip is misleading rather than merely
    // out of date — a coordinator could believe a form is open when it closed an hour ago.
    refetchInterval: 30_000,
  });

  const update = useMutation({
    mutationFn: (body: Record<string, unknown>) => apiPost(`/forms/${formId}/availability`, body),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["availability", formId] });
      setConfirming(false);
      setReason("");
    },
  });

  const countdown = useCountdown(query.data?.nextChange?.at ?? null);

  if (query.isLoading || !query.data) {
    return <p className="text-xs text-muted-foreground">Loading availability…</p>;
  }

  const { availability, nextChange, submissionCount, maxTotalSubmissions } = query.data;
  const chip = STATE_CHIP[availability.state];
  const isStopped = availability.state === "CLOSED_MANUAL";

  return (
    <div className="space-y-3 rounded-lg border bg-card p-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${chip.className}`}>
            {chip.label}
          </span>
          <span className="text-xs text-muted-foreground">
            {submissionCount}
            {maxTotalSubmissions ? ` / ${maxTotalSubmissions}` : ""} submissions
          </span>
        </div>

        {isStopped ? (
          <button
            type="button"
            onClick={() => update.mutate({ manualOverride: "NONE", pauseReason: null })}
            disabled={update.isPending}
            className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary-hover disabled:opacity-50"
          >
            {update.isPending ? "Reopening…" : "Resume collecting"}
          </button>
        ) : (
          <button
            type="button"
            onClick={() => setConfirming(true)}
            className="rounded-md border border-brand-red px-3 py-1.5 text-xs font-medium text-brand-red hover:bg-brand-red/5"
          >
            Stop collecting
          </button>
        )}
      </div>

      {nextChange && countdown && (
        <p className="text-xs text-muted-foreground">
          {nextChange.becomes === "OPEN" ? "Opens" : "Closes"} in <strong>{countdown}</strong> —{" "}
          {new Date(nextChange.at).toLocaleString("en-GB", { timeZone: "Africa/Lagos" })} (Lagos)
        </p>
      )}

      {isStopped && query.data.pauseReason && (
        <p className="rounded-md bg-brand-red/5 p-2 text-xs">
          <span className="font-medium">Stopped:</span> {query.data.pauseReason}
        </p>
      )}

      {confirming && (
        <div className="rounded-md border border-brand-red p-3">
          <p className="text-xs">
            Stopping collection closes the form to applicants immediately. Anyone part-way through
            will not be able to submit. Give a reason — it is recorded in the audit log.
          </p>
          <label
            htmlFor="pause-reason"
            className="mt-2 block text-[11px] uppercase text-muted-foreground"
          >
            Reason (required)
          </label>
          <input
            id="pause-reason"
            className="mt-1 w-full rounded border px-2 py-1.5 text-sm"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="e.g. Suspected duplicate flood from one IP"
          />
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={() => {
                setConfirming(false);
                setReason("");
              }}
              className="rounded-md border px-3 py-1.5 text-xs hover:bg-secondary"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={!reason.trim() || update.isPending}
              onClick={() => update.mutate({ manualOverride: "FORCE_CLOSED", pauseReason: reason })}
              className="rounded-md bg-brand-red px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
            >
              {update.isPending ? "Stopping…" : "Stop collecting now"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
